import * as React from "react";
import { AriaAttributes, AriaRole, ReactNode } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  Box,
  BoxProps,
  Column,
  DivProps,
  getDataProps,
  Heading,
  HeadingVariant,
  Row,
  Space,
  Text,
} from "@stenajs-webui/core";

import { Icon } from "../../icon/Icon";
import styles from "./Banner.module.css";
import cx from "classnames";
import { Spinner } from "../../spinner/Spinner";
import {
  stenaCheck,
  stenaExclamationCircle,
  stenaExclamationTriangle,
  stenaInfoMegaphone,
  stenaInfoSpeech,
} from "../../../../icons/generated/CommonIcons";

export type BannerVariant =
  | "standard"
  | "info"
  | "success"
  | "warning"
  | "error";

export interface BannerProps extends AriaAttributes {
  icon?: IconDefinition;
  headerText?: string;
  headingLevel?: HeadingVariant;
  text?: string;
  loading?: boolean;
  contentRight?: ReactNode;
  variant?: BannerVariant;
  children?: ReactNode;
  contentFlex?: BoxProps["flex"];
  /**
   * The role prop of the element, used for accessibility.
   * If not set, it will default to "status" when variant is success or warning,
   * "alert" when variant is error and otherwise not be set.
   */
  role?: DivProps["role"];
}

const iconPerVariant: Record<BannerVariant, IconDefinition | undefined> = {
  standard: stenaExclamationCircle,
  info: stenaInfoSpeech,
  success: stenaCheck,
  warning: stenaInfoMegaphone,
  error: stenaExclamationTriangle,
};

const leftContentWidth = "56px";

export const Banner: React.FC<BannerProps> = ({
  headerText,
  headingLevel,
  text,
  children,
  contentRight,
  icon,
  variant = "standard",
  loading = false,
  role,
  contentFlex,
  ...rest
}) => {
  const activeRole = role ?? getRoleFromVariant(variant);
  const dataProps = getDataProps(rest);
  const ariaLive = variant === "error" ? "polite" : undefined;

  return (
    <Column
      className={cx(styles.banner, styles[variant])}
      spacing={children || (headerText && text) ? 2 : 1}
      indent={2}
      role={activeRole}
      aria-live={ariaLive}
      {...dataProps}
    >
      <Row justifyContent={"space-between"}>
        <Row flex={"none"} width={leftContentWidth} alignItems={"center"}>
          <div className={styles.iconBackground}>
            {(icon || iconPerVariant[variant] || loading) && (
              <>
                {loading ? (
                  <Spinner size={"tiny"} />
                ) : (
                  <Icon
                    icon={icon ?? iconPerVariant[variant]}
                    size={20}
                    className={styles.icon}
                  />
                )}
              </>
            )}
          </div>
        </Row>
        <Row justifyContent={"space-between"} flexGrow={1} gap>
          <Column justifyContent={"center"} gap>
            {headerText && (
              <Heading variant={"h5"} as={headingLevel}>
                {headerText}
              </Heading>
            )}
            {text && <Text>{text}</Text>}
          </Column>
          {contentRight && (
            <Column justifyContent={"center"}>{contentRight}</Column>
          )}
        </Row>
      </Row>
      {children && (
        <Row>
          <Box minWidth={leftContentWidth} />
          <Box flex={contentFlex}>
            <Space />
            {children}
          </Box>
        </Row>
      )}
    </Column>
  );
};

function getRoleFromVariant(
  variant: BannerVariant | undefined,
): AriaRole | undefined {
  switch (variant) {
    case "error":
      return "alert";
    case "warning":
    case "success":
      return "status";
    default:
      return undefined;
  }
}
